<script>
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { CampaignsService } from '@client/services/campaigns';
  import { TargetingService } from '@client/services/targeting';

  import { GeneralDeleteForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const getCampaigns = async () => {
    const campaigns = await CampaignsService.getAll();
    return campaigns.map((campaign) => ({
      label: campaign.rulename,
      value: campaign.rulename,
      ...campaign
    }));
  };

  const onDeleteCampaign = async (campaign) => {
    const activeTargeting = await TargetingService.get();

    const currentAccountTargeting = activeTargeting.accounts
      .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
      .map(({ name }) => name);

    const updatedAccounts = activeTargeting.accounts.map((account) => {
      if (currentAccountTargeting.includes(account.name)) {
        account.ads = account.ads.filter(({ text }) => text !== campaign.rulename);
      }

      return account;
    });

    const combinedTargeting = {
      accounts: updatedAccounts,
      departments: activeTargeting.departments
    };

    const targetingResponse = await TargetingService.update(combinedTargeting);

    if (targetingResponse?.status >= 400) {
      notify($format('label.FAILURE.DELETE', { values: { type: campaign.rulename } }), 'error');
      throw new Error('Failed to update targeting');
    }

    const campaignDeleteResponse = await CampaignsService.deleteCampaign(campaign);

    if (campaignDeleteResponse?.status >= 400) {
      notify($format('label.FAILURE.DELETE', { values: { type: campaign.rulename } }), 'error');
      throw new Error('Failed to delete campaign');
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: campaign.rulename } }));

    step.data = {
      id: campaign.rulename,
      rollbackData: {
        campaign,
        originalTargeting: activeTargeting
      }
    };
  };

  const rollbackDeleteCampaign = async () => {
    if (!step.data?.rollbackData) {
      throw new Error('No rollback data available');
    }

    const { campaign, originalTargeting } = step.data.rollbackData;

    // Restore the campaign
    const campaignResponse = await CampaignsService.createCampaign(campaign);

    if (campaignResponse?.status >= 400) {
      notify($format('label.FAILURE.CREATE', { values: { type: campaign.rulename } }), 'error');
      throw new Error('Failed to restore campaign');
    }

    // Restore the original targeting
    const targetingResponse = await TargetingService.update(originalTargeting);

    if (targetingResponse?.status >= 400) {
      notify($format('label.FAILURE.RESTORE_TARGETING'), 'error');
      throw new Error('Failed to restore targeting');
    }

    notify($format('label.SUCCESS.ROLLBACK_DELETE'));

    return { success: true };
  };
</script>

<GeneralDeleteForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  rollbackFunction={rollbackDeleteCampaign}
  getFunction={getCampaigns}
  deleteFunction={onDeleteCampaign}
  showPreview={true}
>
  <div slot="preview" let:item>
    {#if item}
      <div class="banner-preview">
        {#if item.adtype === 'image'}
          <img src={item.previewURL} alt={item.filename} loading="lazy" />
        {/if}

        {#if item.adtype === 'text'}
          <div class="text">
            {item.textstring}
          </div>
        {/if}
      </div>
    {/if}
  </div>
</GeneralDeleteForm>

<style lang="scss">
  .banner-preview {
    background-color: var(--color-white);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-base);
    display: flex;
    justify-content: center;
    padding: var(--padding-small);
    position: relative;

    img {
      display: block;
      height: auto;
      min-height: 40px;
    }

    .text {
      padding: var(--padding-base);
    }

    .image-loading {
      min-height: 60px;
      padding: var(--padding-large);
    }
  }
</style>
